import { AttachmentType, ShareMessageContentFragment } from "~/__generated__/graphql"
import { UserAvatar } from "~/ui/user-avatar"
import MessageHeader from "../message-header"
import { MessageAttachments } from "./message-attachments"
import { FormattedAppText } from "~/ui/formatted-app-text"
import { AppText } from "~/ui/app-text"
import { cn } from "~/common/cn"

interface QuotedMessageProps {
  quotedMessage: ShareMessageContentFragment
}

const SUPPORTED_QUOTED_ATTACHMENT_TYPES = [
  AttachmentType.Image,
  AttachmentType.AuraSticker,
  AttachmentType.Giphy,
  AttachmentType.Video,
]

export const QuotedMessage: React.FC<QuotedMessageProps> = ({ quotedMessage }) => {
  const user = quotedMessage.user
  const displayName = user?.displayName || user?.username || "User"
  const hasAttachments = quotedMessage.attachments?.length > 0
  const thumbnails = (quotedMessage.attachments ?? []).filter((attachment) =>
    SUPPORTED_QUOTED_ATTACHMENT_TYPES.includes(attachment.attachmentType)
  )
  return (
    <div className="flex w-fit flex-row justify-between gap-3 rounded-md bg-neutral-200 px-4 py-3">
      <div className="flex flex-col gap-1">
        <div className="flex w-fit flex-row items-center gap-2">
          <UserAvatar avatarUrl={user?.avatarUrl} displayName={displayName} size="xsm" />
          <MessageHeader
            showVerified={user?.verifiedAccount}
            displayName={displayName}
            textVariant="body3-medium"
          />
        </div>
        {quotedMessage?.textContent && (
          <FormattedAppText
            text={quotedMessage.textContent}
            variant="body3-medium"
            className="line-clamp-2 whitespace-pre-wrap"
          />
        )}
        {hasAttachments && thumbnails.length === 0 && (
          <AppText variant="body3-medium">Shared an attachment</AppText>
        )}
      </div>
      {thumbnails.length > 0 && (
        // 204px fits 4 thumbnails (4 × 48px + 3 × 4px gap) in each row. With several thumbnails, each is cropped to a 48px square so that the rows line up
        <div className="flex max-w-[204px] flex-wrap items-center justify-end gap-1">
          {thumbnails.map((attachment) => (
            <div key={attachment.id} className="flex w-12 items-center justify-center">
              <MessageAttachments
                attachments={[attachment]}
                playable={false}
                className={cn("rounded-xs", thumbnails.length > 1 && "h-12")}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
